import { Rocket, X } from 'lucide-react';
import * as React from 'react';
import { formatRelativeTime, formatTime, StartupTimeline } from '@/components/StartupTimeline/StartupTimeline';
import type { WorkspaceProgressStep } from '@/lib/types/rome';

interface StartupHistoryModalProps {
  workspaceName: string;
  steps: WorkspaceProgressStep[];
  onClose: () => void;
}

/**
 * Modal dialog showing the full startup history timeline for a workspace.
 *
 * Renders the StartupTimeline inside a centered overlay with backdrop blur.
 * Closes on backdrop click, Escape key, or the close button.
 */
export function StartupHistoryModal({ workspaceName, steps, onClose }: StartupHistoryModalProps) {
  // Compute start time and total startup duration for the header
  const firstTimestamp = steps.length > 0 ? steps[0].timestamp : 0;
  const runningStep = steps.find((s) => s.step === 'running');
  const startTime = steps.length > 0 ? formatTime(firstTimestamp) : null;
  const startupDuration = runningStep ? formatRelativeTime(firstTimestamp, runningStep.timestamp) : null;
  const hasFailed = steps.some((s) => s.status === 'failed');

  // Close on Escape key
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Workspace lifecycle"
        className="mx-4 w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-gray-100 border-b bg-gray-50/50 px-5 py-3.5 dark:border-white/5 dark:bg-white/5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-500/10 dark:bg-blue-500/15">
              <Rocket className="h-4 w-4 text-blue-500" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm dark:text-gray-100">Workspace Lifecycle</h3>
              <div className="mt-1 flex items-center gap-1.5">
                <span className="truncate rounded-full bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground">
                  {workspaceName}
                </span>
                {startTime && (
                  <span className="shrink-0 rounded-full bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground">
                    {startTime}
                  </span>
                )}
                {startupDuration && !hasFailed && (
                  <span className="shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 font-medium text-[11px] text-emerald-600 tabular-nums dark:text-emerald-400">
                    {startupDuration}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 cursor-pointer rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-gray-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          <StartupTimeline steps={steps} showFooter={false} />
        </div>
      </div>
    </div>
  );
}
